// Persecutor helpers. Pure. No React, no Three.js.
//
// ── Codex (Persecutor) ──
//   "Once on your turn, Persecutor may step toward the closest Evil,
//    or brand an enemy so your cards treat them as Evil this turn."
//
// "Evil" is the synthetic alignment alias (Demon / Monster / Undead.
// see subtypeAliases.js). A branded card carries the manual `branded`
// status, which the Evil filter check honors for the rest of the turn.
// The orchestrator (avatarAbilities) owns the choice prompt, the
// cell-pick, and the brand target-pick; this module owns the
// closest-Evil math.

import { matchesFilter } from '../../cardFilter';

/**
 * Cells of every Evil card on the board, as seen by `persecutor`
 * (branded enemies count. `matchesFilter` folds the brand into the
 * Evil check). `boardCards` is [{ card, cell }] for every card with a
 * resolvable cell; the persecutor itself is excluded.
 */
export function evilCells(persecutor, boardCards) {
  const out = [];
  for (const { card, cell } of boardCards || []) {
    if (!card || !cell || card.id === persecutor.id) continue;
    if (matchesFilter(card, { subtype: 'evil' }, { source: persecutor })) out.push(cell);
  }
  return out;
}

function manhattan(a, b) {
  return Math.abs(a.col - b.col) + Math.abs(a.row - b.row);
}

/**
 * The subset of 1-step reach cells that move strictly closer to the
 * closest Evil. `reachKeys` is the movement engine's `Set<"col,row">`;
 * ties for closest are all honored (a step toward ANY of the tied
 * closest cards qualifies).
 *
 * @returns Set<"col,row">. Empty when there is no Evil or no
 *   distance-reducing step.
 */
export function stepCellsTowardClosestEvil(reachKeys, fromCell, targetCells) {
  const out = new Set();
  if (!fromCell || !targetCells || targetCells.length === 0) return out;
  const best = Math.min(...targetCells.map((c) => manhattan(fromCell, c)));
  const closest = targetCells.filter((c) => manhattan(fromCell, c) === best);
  for (const key of reachKeys || []) {
    const [col, row] = key.split(',').map(Number);
    const stepCell = { col, row };
    if (closest.some((c) => manhattan(stepCell, c) < best)) out.add(key);
  }
  return out;
}
